import { CarrierType } from '@planning/app/api'
import { ContainerJourneyItemStore } from '@planning/pages/Issues/Stores/ContainerJourneyItemStore'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { RailVisitItemStore } from '@planning/rt-stores/railVisit/RailVisitItemStore'
import _ from 'lodash'
import { computed, makeObservable } from 'mobx'
import { BulkEditSelectionDto } from '../tableConfigs/useRailVisitTableActions'

export class RailVisitBulkEditViewStore {
  constructor(
    private readonly containerJourneyItemStore: ContainerJourneyItemStore,
    private readonly railVisitItemStore: RailVisitItemStore,
    private readonly railVisitId: number,
    private readonly bulkEditData: BulkEditSelectionDto[],
  ) {
    makeObservable(this, {
      railVisitItem: computed,

      containerJourneys: computed,

      inboundOrders: computed,
      outboundOrders: computed,

      inboundUnknownCount: computed,
      inboundTruckCount: computed,
      inboundTrainCount: computed,
      inboundVesselCount: computed,

      outboundUnknownCount: computed,
      outboundTruckCount: computed,
      outboundTrainCount: computed,
      outboundVesselCount: computed,

      hasAtLeastTwoDifferentInboundCarrierTypes: computed,
      hasAtLeastTwoDifferentOutboundCarrierTypes: computed,
    })
  }

  get railVisitItem(): IRailVisitItem | undefined {
    return this.railVisitItemStore.elements[this.railVisitId]
  }

  get containerJourneys() {
    return _(this.containerJourneyItemStore.elements)
      .filter(cj => {
        return this.bulkEditData.some(bulk => bulk.containerJourneyId === cj.id)
      })
      .value()
  }

  get inboundOrders() {
    return this.containerJourneys.filter(cj => cj.inboundOrder).map(cj => cj.inboundOrder!)
  }

  get outboundOrders() {
    return this.containerJourneys.filter(cj => cj.outboundOrder).map(cj => cj.outboundOrder!)
  }

  get inboundUnknownCount(): number {
    return (
      this.getInboundOrdersCarrierVisitCountOfType(CarrierType.Universal) +
      this.getInboundOrdersCarrierVisitCountOfType(undefined)
    )
  }

  get inboundTruckCount(): number {
    return this.getInboundOrdersCarrierVisitCountOfType(CarrierType.Truck)
  }

  get inboundTrainCount(): number {
    return this.getInboundOrdersCarrierVisitCountOfType(CarrierType.Train)
  }

  get inboundVesselCount(): number {
    return this.getInboundOrdersCarrierVisitCountOfType(CarrierType.Vessel)
  }

  get outboundUnknownCount(): number {
    return (
      this.getOutboundOrdersCarrierVisitCountOfType(CarrierType.Universal) +
      this.getOutboundOrdersCarrierVisitCountOfType(undefined)
    )
  }

  get outboundTruckCount(): number {
    return this.getOutboundOrdersCarrierVisitCountOfType(CarrierType.Truck)
  }

  get outboundTrainCount(): number {
    return this.getOutboundOrdersCarrierVisitCountOfType(CarrierType.Train)
  }

  get outboundVesselCount(): number {
    return this.getOutboundOrdersCarrierVisitCountOfType(CarrierType.Vessel)
  }

  get hasAtLeastTwoDifferentInboundCarrierTypes(): boolean {
    return this.hasAtLeastTwoDifferentCarrierTypes([
      this.inboundUnknownCount,
      this.inboundTruckCount,
      this.inboundTrainCount,
      this.inboundVesselCount,
    ])
  }

  get hasAtLeastTwoDifferentOutboundCarrierTypes(): boolean {
    return this.hasAtLeastTwoDifferentCarrierTypes([
      this.outboundUnknownCount,
      this.outboundTruckCount,
      this.outboundTrainCount,
      this.outboundVesselCount,
    ])
  }

  hasAtLeastTwoDifferentCarrierTypes(carrierTypeCounts: number[]): boolean {
    return carrierTypeCounts.filter(count => count > 0).length >= 2
  }

  getInboundOrdersCarrierVisitCountOfType(carrierType?: CarrierType): number {
    return this.getOrdersCarrierVisitCountOfType(this.inboundOrders, carrierType)
  }

  getOutboundOrdersCarrierVisitCountOfType(carrierType?: CarrierType): number {
    return this.getOrdersCarrierVisitCountOfType(this.outboundOrders, carrierType)
  }

  getOrdersCarrierVisitCountOfType(orderItems: IOrderItem[], carrierType?: CarrierType) {
    return orderItems?.filter(order => order.data.carrierVisitType === carrierType)?.length ?? 0
  }
}
